import React from "react";
import { Controller } from "react-hook-form";

import { SegmentedControl } from "../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  HelperText,
} from "../../../../components/MobileForm/styles";
import { FormInput, InputDate } from "../../../../components/Input";
import { AccountMultiSelect } from "../../../../components/AccountMultiSelect";
import { MODE_OPTIONS, MODE_SEGMENTS } from "./constants";
import { MobileSavingsGoalFormProps } from "./types";

export const MobileSavingsGoalForm: React.FC<MobileSavingsGoalFormProps> = ({
  control,
  accounts,
  isSubmitting,
}) => {
  return (
    <>
      <Card>
        <CardLabel htmlFor="mobile-savings-goal-name">Goal name *</CardLabel>
        <FormInput
          id="mobile-savings-goal-name"
          required
          name="name"
          control={control}
          rules={{
            required: "Name is required!",
            minLength: {
              value: 3,
              message: "Name should have more than 3 letters!",
            },
          }}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="e.g. Emergency Fund"
          autoFocus={true}
          variant="title"
        />

        <CardLabel htmlFor="mobile-savings-goal-target-amount">
          Target amount (€) *
        </CardLabel>
        <FormInput
          id="mobile-savings-goal-target-amount"
          required
          name="targetAmount"
          control={control}
          type="number"
          rules={{
            required: "Target amount is required!",
            min: { value: 1, message: "Target must be greater than 0!" },
          }}
          handleClear={false}
          disabled={isSubmitting}
          placeholder="e.g. 10000"
        />
      </Card>

      <Card>
        <CardLabel htmlFor="mobile-savings-goal-target-date">
          Target date *
        </CardLabel>
        <InputDate
          id="mobile-savings-goal-target-date"
          name="targetDate"
          control={control}
          rules={{ required: "Target date is required!" }}
          disabled={isSubmitting}
          placeholder="YYYY/MM/DD"
        />
        <HelperText>When you want to reach this goal.</HelperText>

        <CardLabel>Goal type *</CardLabel>
        <Controller
          name="mode"
          control={control}
          rules={{ required: "Goal type is required!" }}
          render={({ field }) => (
            <SegmentedControl
              options={MODE_SEGMENTS}
              selectedValue={field.value?.value ?? ""}
              disabled={isSubmitting}
              onChange={(value) => {
                const option = MODE_OPTIONS.find(
                  (mode) => mode.value === value,
                );
                field.onChange(option);
              }}
            />
          )}
        />
        <HelperText>
          Track a total balance, or an amount saved this year.
        </HelperText>
      </Card>

      <Card>
        <CardLabel>Linked savings accounts *</CardLabel>
        <Controller
          name="accountIds"
          control={control}
          rules={{
            validate: (value: number[]) =>
              value.length > 0 || "Link at least one account!",
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <AccountMultiSelect
                accounts={accounts}
                value={value}
                onChange={onChange}
                disabled={isSubmitting}
                emptyLabel="No savings accounts available."
              />
              {error && <HelperText>{error.message}</HelperText>}
            </>
          )}
        />
        <HelperText>Progress is measured from these accounts.</HelperText>
      </Card>
    </>
  );
};
